import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import styles from "./index.module.css";
import { contactClientCareSchema } from "../../yup";

interface ContactData {
  firstName: string;
  lastName: string;
  email: string;
}

interface ContactClientCareCardProps {
  initialEmail: string;
  isContactSubmitted: boolean;
  isSubmittingContact: boolean;
  contactError: string;
  onSubmitForm: (data: ContactData) => void;
  onCancel: () => void;
  onClearError: () => void;
  title: string;
  description: string;
}

const ContactClientCareCard: React.FC<ContactClientCareCardProps> = ({
  initialEmail,
  isContactSubmitted,
  isSubmittingContact,
  contactError,
  onSubmitForm,
  onCancel,
  onClearError,
  title,
  description,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactData>({
    resolver: yupResolver(contactClientCareSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: initialEmail || "",
    },
  });

  const handleChange = () => {
    if (contactError) onClearError();
  };

  return (
    <div className="w-full max-w-sm flex flex-col">
      {isContactSubmitted ? (
        <>
          <h2 className="text-2xl font-semibold text-[#1f2937] mb-4 text-center font-['Proxima_Nova']">
            Thank You
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6 font-['Proxima_Nova']">
            Thank you. Someone will get in touch with you soon.
          </p>
          <button
            onClick={onCancel}
            className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md font-['Proxima_Nova']"
          >
            Go Back to Sign In
          </button>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-semibold text-[#1f2937] mb-2 text-center font-['Proxima_Nova']">
            {title}
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6 font-['Proxima_Nova']">
            {description}
          </p>

          <form
            onSubmit={handleSubmit(onSubmitForm)}
            className="w-full flex flex-col gap-4"
          >
            <div className="relative w-full">
              <input
                id="firstName-input"
                placeholder=" "
                type="text"
                {...register("firstName", { onChange: handleChange })}
                disabled={isSubmittingContact}
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
              />
              <label
                htmlFor="firstName-input"
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.firstName ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                First Name*
              </label>
            </div>
            {errors.firstName && (
              <span className="-mt-3 block text-xs font-medium text-red-500">
                {errors.firstName.message}
              </span>
            )}

            <div className="relative w-full">
              <input
                id="lastName-input"
                placeholder=" "
                type="text"
                {...register("lastName", { onChange: handleChange })}
                disabled={isSubmittingContact}
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
              />
              <label
                htmlFor="lastName-input"
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.lastName ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                Last Name*
              </label>
            </div>
            {errors.lastName && (
              <span className="-mt-3 block text-xs font-medium text-red-500">
                {errors.lastName.message}
              </span>
            )}
            
            <div className="relative w-full">
              <input
                id="forgotEmail"
                type="email"
                {...register("email")}
                readOnly
                className="w-full min-h-[52px] rounded-lg border bg-gray-100 px-4 py-0 text-base text-gray-500 outline-none focus:ring-0 focus:shadow-none border-gray-300 cursor-not-allowed"
              />
            </div>

            {contactError && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {contactError}
              </span>
            )}

            <button
              type="submit"
              disabled={isSubmittingContact}
              className="bg-[#4D5874] relative mt-2 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
            >
              <span className={isSubmittingContact ? "opacity-0" : ""}>
                Submit
              </span>
              {isSubmittingContact && (
                <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
            </button>
            
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onCancel}
                className="text-xs font-semibold text-[#3B755E] underline font-['Proxima_Nova']"
              >
                Cancel / Go Back
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ContactClientCareCard;